// Phase 85.A.90 — Rating Video Upload Modal.
//
// Mở khi user fail OCR ảnh điểm trình + tick checkbox "Tôi chưa từng tham
// gia PVNA/Baseline/DUPR". User có 2 cách nộp:
//   1. Upload trực tiếp video MP4/MOV/WebM, ≤1GB, 5-30 phút.
//   2. Dán link Google Drive.
//
// Trước khi cho phép nộp: user PHẢI tick đủ 3 cam kết:
//   - "Tôi xác nhận video này có TÔI ĐANG THI ĐẤU Pickleball"
//   - "Video được quay trong vòng 30 ngày gần nhất"
//   - "Tôi đã thi đấu đúng với khả năng thực tế"
//
// Backend (DO Spaces + ffprobe validate ≥5 phút) sẽ check duration sau
// khi upload. Max 3 lần upload — sau đó user phải chờ admin review.
import { useEffect, useRef, useState } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  X,
  VideoCamera,
  CheckCircle,
  Link as LinkIcon,
  CloudArrowUp,
  Warning,
  ArrowLeft,
  ArrowRight,
  YoutubeLogo,
} from "@phosphor-icons/react";

import { api } from "../../../lib/api";
import UploadProgressBar from "../../video/UploadProgressBar";
import { markUploadActive } from "../../../lib/uploadActivity";

// Phase 85.A.141 — Sticky compact header báo % + speed MB/s + ETA + bytes
// trong khi upload đang chạy. User yêu cầu để biết tiến trình thực sự đang
// "chạy", giảm rủi ro user vô tình đóng tab vì nghĩ rằng modal bị treo.
function _fmtMB(bytes) {
  const mb = bytes / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
}

function _fmtEta(seconds) {
  if (!isFinite(seconds) || seconds <= 0) return "—";
  const s = Math.ceil(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r.toString().padStart(2, "0")}s`;
}

function StickyUploadHeader({ progress }) {
  const { pct = 0, bytesUploaded = 0, bytesTotal = 0, startedAt = 0, stage, label } = progress || {};
  // Phase 85.A.141 — Tick mỗi 1s để speed/ETA cập nhật mượt giữa các lần
  // setProgress() từ parent (mỗi khi 1 part xong).
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (stage !== "uploading") return undefined;
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [stage]);
  const elapsed = startedAt ? (Date.now() - startedAt) / 1000 : 0;
  const speedBps = elapsed > 0 ? bytesUploaded / elapsed : 0;
  const speedMBs = speedBps / (1024 * 1024);
  const remaining = bytesTotal - bytesUploaded;
  const etaSec = stage === "uploading" && speedBps > 0 ? remaining / speedBps : NaN;
  const safePct = Math.max(0, Math.min(100, pct));
  return (
    <div
      className="px-4 py-2.5 bg-gradient-to-r from-blue-500/15 via-emerald-500/8 to-transparent border-b border-emerald-400/25 shrink-0 sticky top-0 z-10"
      data-testid="upload-sticky-header"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-[10.5px] uppercase font-black tracking-wider text-emerald-200/85 truncate">
          {label || "Đang xử lý…"}
        </div>
        <div className="text-[11.5px] font-black text-emerald-200 tabular-nums shrink-0">
          {safePct.toFixed(1)}%
        </div>
      </div>
      <div className="h-2 rounded-full bg-white/8 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-400 via-emerald-400 to-emerald-300 transition-all duration-300 ease-out"
          style={{ width: `${Math.max(2, safePct)}%` }}
          data-testid="upload-sticky-bar"
        />
      </div>
      <div className="mt-1 flex items-center justify-between text-[10px] text-white/60 tabular-nums font-mono">
        <span data-testid="upload-bytes">
          <span className="text-white/80 font-bold">{_fmtMB(bytesUploaded)}</span>
          <span className="text-white/40"> / {_fmtMB(bytesTotal)}</span>
        </span>
        <span className="flex items-center gap-2">
          <span data-testid="upload-speed">
            {speedMBs > 0 ? `${speedMBs.toFixed(2)} MB/s` : "— MB/s"}
          </span>
          <span className="text-white/30">·</span>
          <span data-testid="upload-eta">
            {stage === "uploading" ? `ETA ${_fmtEta(etaSec)}` : (stage === "analyzing" ? "Đang xử lý server…" : "")}
          </span>
        </span>
      </div>
    </div>
  );
}

const MAX_MB = 1024; // 1GB
const MIN_SECONDS = 3 * 60;
const MAX_SECONDS = 15 * 60;
const NOTES_MIN = 20;
const ACCEPT = "video/mp4,video/quicktime,video/webm,video/x-matroska,video/mkv";

const COMMITMENT_ITEMS_DEFAULT = [
  {
    key: "no_other_platform",
    label: "Tôi xác nhận chưa từng tham gia PVNA/BaseLine/DUPR đánh giải nào.",
  },
  {
    key: "acknowledge_account_suspension",
    label: "Tôi cam kết chịu trách nhiệm và hiểu rằng tôi có thể bị tạm khoá tài khoản nếu như BTC phát hiện thông tin tôi đã gửi là không chính xác.",
    danger: true,
  },
];

// Phase 85.A.108 — Variant cho card "Xác thực điểm bằng Video thi đấu" trong
// trang Personal. 3 cam kết theo yêu cầu user (IMG_8052). Tất cả keys đều
// map sang `no_other_platform` + `acknowledge_account_suspension` ở backend
// khi user tick đủ — tương thích 100% với schema rating-video hiện tại.
const COMMITMENT_ITEMS_PERSONAL = [
  {
    key: "no_other_platform",
    label: "Tôi chưa từng tham gia bất cứ giải của hệ thống App nào có chấm điểm trình.",
  },
  {
    key: "pickleague_decision",
    label: "Tôi hiểu rằng việc xác minh điểm trình bằng video sẽ do Pickleague quyết định.",
  },
  {
    key: "acknowledge_account_suspension",
    label: "Tôi cam kết rằng video tải lên hoàn toàn trung thực và đúng sự thật, nếu cố tình gian lận có thể bị hạn chế tài khoản.",
    danger: true,
  },
];

export default function RatingVideoUploadModal({ open, onClose, onSubmitted, variant = "default" }) {
  // Phase 85.A.108 — variant="personal_card" hiển thị 3 cam kết riêng + tiêu
  // đề submit "Tải Video lên". Default flow (mở từ OCR fail) giữ 2 cam kết cũ.
  const isPersonal = variant === "personal_card";
  const commitmentItems = isPersonal ? COMMITMENT_ITEMS_PERSONAL : COMMITMENT_ITEMS_DEFAULT;
  const buildInitialCommitments = () => commitmentItems.reduce((acc, it) => {
    acc[it.key] = false;
    return acc;
  }, {});

  const [step, setStep] = useState(1); // 1 = guide, 2 = upload
  // Phase 85.A.162 — Gộp YT + FB + Drive thành 1 tab "link" duy nhất với
  // auto-detect (đỡ chật UI). Tab `upload` giữ riêng vì có file picker khác.
  const [mode, setMode] = useState("upload"); // upload | link
  const [commitments, setCommitments] = useState(buildInitialCommitments);
  const [file, setFile] = useState(null);
  // Phase 85.A.162 — Single input cho cả 3 nguồn link.
  const [linkUrl, setLinkUrl] = useState("");
  // Phase 85.A.159 — Giữ lại cho backward-compat các phần code khác.
  const [driveUrl, setDriveUrl] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Phase 85.A.122 — Highlight nhấp nháy ô notes khi user nhấn nộp mà chưa
  // điền đủ mô tả. Ref dùng để focus + scroll.
  const [notesShake, setNotesShake] = useState(false);
  const notesRef = useRef(null);
  const highlightNotes = () => {
    toast.error("Bạn cần điền mô tả để BTC biết bạn là ai trong video.");
    setNotesShake(true);
    try {
      notesRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => notesRef.current?.focus(), 250);
    } catch { /* noop */ }
    setTimeout(() => setNotesShake(false), 1200);
  };
  const [progress, setProgress] = useState({ stage: "idle", pct: 0, label: "", bytesUploaded: 0, bytesTotal: 0, startedAt: 0 });
  const [serverError, setServerError] = useState(null);
  const fileRef = useRef(null);
  // Phase 85.A.166 — Auto-paste link từ clipboard khi user mở tab "Link".
  // Chỉ auto-paste 1 LẦN/lần mở modal để tránh ghi đè khi user xoá input.
  // Phải call navigator.clipboard.readText() trực tiếp trong onClick handler
  // để giữ user-gesture context (iOS Safari 13.1+ yêu cầu).
  const autoPastedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setMode("upload");
      setCommitments(buildInitialCommitments());
      setFile(null);
      setDriveUrl("");
      setYoutubeUrl("");
      setLinkUrl("");
      setNotes("");
      setSubmitting(false);
      setProgress({ stage: "idle", pct: 0, label: "", bytesUploaded: 0, bytesTotal: 0, startedAt: 0 });
      setServerError(null);
      autoPastedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, variant]);

  // Phase 85.A.150 — Global flag để các gate context khác (avatar / pending
  // rating proposal) KHÔNG re-fetch & remount React tree trong lúc user
  // đang ở modal upload. Đặc biệt quan trọng trên iOS Safari: file picker
  // native trigger window focus event khi đóng → polling fetch → state đổi
  // → AnimatePresence ở 1 sibling modal có thể đẩy upload modal "biến mất".
  useEffect(() => {
    markUploadActive(!!open);
    return () => markUploadActive(false);
  }, [open]);

  // Phase 85.A.140 — Chặn user vô tình đóng modal khi đang upload:
  //   • ESC key → preventDefault.
  //   • beforeunload (F5 / đóng tab / back navigation) → confirm dialog.
  // Modal đã bỏ click-backdrop-to-close + ẩn nút X khi submitting. Đảm bảo
  // request không bị abort giữa chừng gây video hỏng hoặc duplicate.
  useEffect(() => {
    if (!open || !submitting) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "Video đang được tải lên. Đóng trang sẽ huỷ tiến trình. Bạn chắc chắn?";
      return e.returnValue;
    };
    window.addEventListener("keydown", onKey, { capture: true });
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("keydown", onKey, { capture: true });
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [open, submitting]);

  const allCommitted = commitmentItems.every((it) => !!commitments[it.key]);

  // Phase 85.A.108 — Build payload commitments cho BE: luôn set
  // no_other_platform + acknowledge_account_suspension = true khi user tick
  // đủ commitmentItems (vì BE chỉ enforce 2 key này).
  const buildBackendCommitments = () => ({
    no_other_platform: true,
    acknowledge_account_suspension: true,
    submitted_within_30_days: false,
    accurate_skill: false,
  });

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Video quá lớn (>${MAX_MB}MB).`);
      return;
    }
    setFile(f);
    setServerError(null);
  };

  const submitDrive = async () => {
    if (!allCommitted) {
      toast.error("Vui lòng tick đầy đủ các cam kết.");
      return;
    }
    if (notes.trim().length < NOTES_MIN) {
      highlightNotes();
      return;
    }
    const url = driveUrl.trim();
    if (!/^https?:\/\/(drive|docs)\.google\.com\//i.test(url)) {
      toast.error("Link không hợp lệ — chỉ chấp nhận URL Google Drive.");
      return;
    }
    setSubmitting(true);
    setServerError(null);
    // Phase 85.A.151 — Pseudo-progress UI: backend xử lý đồng bộ ~30-180s
    // (download Drive → upload Spaces → thumbnail → approve), không có
    // websocket nên simulate progress dựa trên elapsed time để user không
    // tưởng app treo. Cap 90% chờ response thực sự về.
    const t0 = Date.now();
    setProgress({ stage: "analyzing", pct: 3, label: "Đang tải file từ Google Drive về VPS…", bytesUploaded: 0, bytesTotal: 0, startedAt: t0 });
    const driveTimer = setInterval(() => {
      const elapsed = (Date.now() - t0) / 1000;
      // Ease-out tăng dần tới ~90% trong khoảng 90s.
      const pct = Math.min(90, Math.round(90 * (1 - Math.exp(-elapsed / 35))));
      let label = "Đang tải file từ Google Drive về VPS…";
      if (elapsed > 25) label = "Đang upload sang DO Spaces…";
      if (elapsed > 55) label = "Đang tạo thumbnail + chuẩn bị HLS…";
      if (elapsed > 90) label = "Sắp xong, vui lòng đợi thêm chút…";
      setProgress((p) => ({ ...p, pct, label }));
    }, 700);
    try {
      const r = await api.post("/athlete/rating-video/submit-drive", {
        drive_url: url,
        commitments: buildBackendCommitments(),
        notes: notes.trim(),
      });
      clearInterval(driveTimer);
      setProgress({ stage: "done", pct: 100, label: "Hoàn tất! Đã gửi BTC review.", bytesUploaded: 0, bytesTotal: 0, startedAt: t0 });
      toast.success("Đã gửi link video. BTC sẽ review trong 24-72h.");
      // Đợi 0.6s để user thấy bar emerald 100% rồi mới đóng.
      await new Promise((res) => setTimeout(res, 600));
      onSubmitted?.(r?.data?.video);
      onClose?.();
    } catch (err) {
      clearInterval(driveTimer);
      const msg = err?.response?.data?.detail || "Gửi link thất bại.";
      setServerError(msg);
      setProgress({ stage: "idle", pct: 0, label: "", bytesUploaded: 0, bytesTotal: 0, startedAt: 0 });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Phase 85.A.162-163 — Auto-detect link source. Return: "youtube" | "facebook" | "tiktok" | "drive" | "unknown".
  const detectLinkSource = (url) => {
    const u = (url || "").trim();
    if (!u) return "unknown";
    if (/drive\.google\.com\//i.test(u)) return "drive";
    if (/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)[\w-]{11}/i.test(u)) return "youtube";
    if (/(?:facebook\.com|fb\.watch|fb\.com)\//i.test(u)) return "facebook";
    if (/(?:www\.|vm\.|vt\.|m\.)?tiktok\.com\//i.test(u)) return "tiktok";
    return "unknown";
  };
  const linkSource = detectLinkSource(linkUrl);

  // Phase 85.A.166 — Read clipboard & auto-paste nếu chứa link hợp lệ.
  // QUAN TRỌNG: call clipboard.readText() trực tiếp trong onClick để giữ
  // user gesture (iOS Safari 13.1+). Effect-based approach SẼ fail vì lost
  // gesture context. `force=true` để user có thể bấm nút "Dán từ clipboard"
  // thủ công khi muốn override input hiện tại.
  const tryPasteFromClipboard = async (force = false) => {
    if (!force && (autoPastedRef.current || linkUrl.trim())) return;
    autoPastedRef.current = true;
    try {
      if (!navigator?.clipboard?.readText) return;
      const raw = await navigator.clipboard.readText();
      const txt = (raw || "").trim();
      if (!txt) return;
      const src = detectLinkSource(txt);
      if (src === "unknown") {
        if (force) toast.error("Clipboard không chứa link YouTube / Facebook / TikTok / Drive.");
        return;
      }
      setLinkUrl(txt);
      const label = src === "youtube" ? "YouTube" : src === "facebook" ? "Facebook" : src === "tiktok" ? "TikTok" : "Google Drive";
      toast.success(`Đã dán link ${label} từ clipboard.`);
    } catch {
      // Permission denied / không support → silent fail.
      if (force) toast.error("Không đọc được clipboard. Vui lòng dán thủ công.");
    }
  };

  // Phase 85.A.162 — Submit link (YT/FB/Drive) — auto route theo source.
  const submitLink = async () => {
    if (!allCommitted) {
      toast.error("Vui lòng tick đầy đủ các cam kết.");
      return;
    }
    if (notes.trim().length < NOTES_MIN) {
      highlightNotes();
      return;
    }
    const url = linkUrl.trim();
    const src = detectLinkSource(url);
    if (src === "unknown") {
      toast.error("Link không thuộc YouTube / Facebook / TikTok / Google Drive.");
      return;
    }
    setSubmitting(true);
    setServerError(null);
    try {
      let r;
      if (src === "drive") {
        // Drive cần background download tới DO → reuse submit-drive endpoint.
        r = await api.post("/athlete/rating-video/submit-drive", {
          drive_url: url,
          commitments: buildBackendCommitments(),
          notes: notes.trim(),
        });
        toast.success("Đã nhận link Drive. Hệ thống tải về & BTC sẽ review trong 24-72h.");
      } else {
        // YT/FB/TikTok → embed-only, backend auto-detect qua submit-link.
        r = await api.post("/athlete/rating-video/submit-link", {
          url,
          commitments: buildBackendCommitments(),
          notes: notes.trim(),
        });
        const label = src === "youtube" ? "YouTube" : src === "facebook" ? "Facebook" : "TikTok";
        toast.success(`Đã gửi link ${label}. BTC sẽ review trong 24-72h.`);
      }
      onSubmitted?.(r?.data?.video);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Gửi link thất bại.";
      setServerError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const submitUpload = async () => {
    if (!allCommitted) {
      toast.error("Vui lòng tick đầy đủ các cam kết.");
      return;
    }
    if (!file) {
      toast.error("Vui lòng chọn file video.");
      return;
    }
    if (notes.trim().length < NOTES_MIN) {
      highlightNotes();
      return;
    }
    setSubmitting(true);
    setServerError(null);

    // Phase 85.A.102 — MULTIPART UPLOAD. Bypass iOS Safari fetch() "object can
    // not be cloned" bug bằng cách chia file thành chunks 5MB, mỗi chunk PUT
    // riêng. Mỗi chunk là Blob nhỏ độc lập → không trigger clone bug.
    const PART_SIZE = 5 * 1024 * 1024; // 5MB
    const CONCURRENCY = 3;
    const contentType = file.type || "video/mp4";
    const totalParts = Math.ceil(file.size / PART_SIZE);
    const totalBytes = file.size;
    const uploadStartedAt = Date.now();
    let initData = null;
    let analyzeTimer = null;

    try {
      // STEP 1: Init multipart upload.
      setProgress({ stage: "presigning", pct: 1, label: "Đang khởi tạo upload đa phần…", bytesUploaded: 0, bytesTotal: totalBytes, startedAt: uploadStartedAt });
      const initResp = await api.post("/athlete/rating-video/multipart/init", {
        filename: file.name,
        content_type: contentType,
        size_bytes: file.size,
      });
      initData = initResp?.data;
      if (!initData?.upload_id || !initData?.storage_key) {
        throw new Error("Không khởi tạo được upload.");
      }
      const { upload_id, storage_key } = initData;

      // STEP 2: Upload từng part, parallel 3 cùng lúc.
      setProgress({
        stage: "uploading",
        pct: 3,
        label: `Đang tải lên (0/${totalParts} phần)…`,
        bytesUploaded: 0,
        bytesTotal: totalBytes,
        startedAt: uploadStartedAt,
      });
      const uploadedParts = new Array(totalParts);
      const completedFlags = new Array(totalParts).fill(false);
      const partSizes = new Array(totalParts).fill(0);

      const uploadPart = async (idx) => {
        const partNumber = idx + 1;
        const start = idx * PART_SIZE;
        const end = Math.min(start + PART_SIZE, file.size);
        const chunk = file.slice(start, end);
        partSizes[idx] = end - start;
        const signResp = await api.post("/athlete/rating-video/multipart/sign-part", {
          storage_key,
          upload_id,
          part_number: partNumber,
        });
        const url = signResp?.data?.url;
        if (!url) throw new Error(`Không sign được URL cho part ${partNumber}.`);
        const putResp = await fetch(url, {
          method: "PUT",
          body: chunk,
          mode: "cors",
          credentials: "omit",
        });
        if (!putResp.ok) {
          const snippet = await putResp.text().catch(() => "");
          throw new Error(`Part ${partNumber} bị từ chối (HTTP ${putResp.status}). ${snippet.slice(0, 120)}`);
        }
        const etag = putResp.headers.get("ETag") || putResp.headers.get("etag");
        if (!etag) throw new Error(`Part ${partNumber} thiếu ETag header.`);
        uploadedParts[idx] = { PartNumber: partNumber, ETag: etag };
        completedFlags[idx] = true;
        const done = completedFlags.filter(Boolean).length;
        const bytesUploaded = completedFlags.reduce(
          (sum, ok, i) => sum + (ok ? partSizes[i] : 0), 0,
        );
        const pct = Math.min(85, Math.round(3 + (done / totalParts) * 80));
        setProgress({
          stage: "uploading",
          pct,
          label: `Đang tải lên (${done}/${totalParts} phần)…`,
          bytesUploaded,
          bytesTotal: totalBytes,
          startedAt: uploadStartedAt,
        });
      };

      // Worker pool: CONCURRENCY parallel.
      let next = 0;
      const workers = Array.from({ length: Math.min(CONCURRENCY, totalParts) }, async () => {
        while (true) {
          const idx = next++;
          if (idx >= totalParts) break;
          let attempt = 0;
          while (true) {
            try {
              await uploadPart(idx);
              break;
            } catch (err) {
              attempt += 1;
              if (attempt >= 3) throw err;
              await new Promise((r) => setTimeout(r, 500 * 2 ** (attempt - 1)));
            }
          }
        }
      });
      await Promise.all(workers);

      // STEP 3: Complete + ffprobe + save doc.
      setProgress({ stage: "analyzing", pct: 88, label: "Đang ghép các phần và phân tích video…", bytesUploaded: totalBytes, bytesTotal: totalBytes, startedAt: uploadStartedAt });
      const startedAt = Date.now();
      analyzeTimer = setInterval(() => {
        const elapsed = (Date.now() - startedAt) / 1000;
        const targetPct = Math.min(97, 88 + elapsed * 0.8);
        const label = elapsed < 6
          ? "Đang ghép các phần và phân tích video…"
          : "Đang lưu vào hệ thống…";
        setProgress({ stage: "analyzing", pct: targetPct, label, bytesUploaded: totalBytes, bytesTotal: totalBytes, startedAt: uploadStartedAt });
      }, 500);
      const r = await api.post(
        "/athlete/rating-video/multipart/complete",
        {
          storage_key,
          upload_id,
          filename: file.name,
          content_type: contentType,
          size_bytes: file.size,
          commitments: buildBackendCommitments(),
          notes: notes.trim(),
          parts: uploadedParts,
        },
        { timeout: 600_000 },
      );
      clearInterval(analyzeTimer);
      analyzeTimer = null;
      setProgress({ stage: "done", pct: 100, label: "Hoàn tất!", bytesUploaded: totalBytes, bytesTotal: totalBytes, startedAt: uploadStartedAt });
      toast.success("Đã gửi video. BTC sẽ review trong 24-72h.");
      onSubmitted?.(r?.data?.video);
      setTimeout(() => onClose?.(), 700);
    } catch (err) {
      if (analyzeTimer) clearInterval(analyzeTimer);
      // Abort multipart trên BE để cleanup parts đã upload.
      if (initData?.upload_id && initData?.storage_key) {
        try {
          await api.post("/athlete/rating-video/multipart/abort", {
            storage_key: initData.storage_key,
            upload_id: initData.upload_id,
          });
        } catch { /* ignore */ }
      }
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || err.message || "Upload thất bại.");
      setServerError(msg);
      setProgress({ stage: "error", pct: 0, label: "", bytesUploaded: 0, bytesTotal: 0, startedAt: 0 });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="pl-fixed-fullscreen z-[420] flex items-end sm:items-center justify-center bg-black/85 backdrop-blur-md px-3 pb-3 sm:pb-0"
          data-testid="rating-video-upload-modal"
        >
          <motion.div
            initial={{ y: 32, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 32, scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.24 }}
            className="w-full max-w-lg rounded-3xl border border-blue-400/35 bg-gradient-to-br from-[#0B1428] via-[#0F1623] to-[#0A0F1C] shadow-[0_24px_80px_rgba(59,130,246,0.4)] overflow-hidden flex flex-col max-h-[92dvh] min-h-0"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 py-4 flex items-start gap-3 border-b border-white/8 bg-gradient-to-r from-blue-500/12 to-transparent shrink-0 sticky top-0">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-500/35 to-cyan-500/15 border border-blue-400/45 flex items-center justify-center shrink-0">
                <VideoCamera size={20} weight="fill" className="text-blue-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-blue-300/85">
                  Bước {step} / 2 · Video chấm điểm trình
                </div>
                <div className="text-[15px] font-black text-white leading-tight" data-testid="rating-video-modal-title">
                  {step === 1 ? "Hướng dẫn nộp video thi đấu" : "Tải video / dán link"}
                </div>
                <div className="text-[11px] text-white/55 leading-snug mt-0.5">
                  {step === 1 ? "Đọc kỹ trước khi nộp." : "Chọn 1 trong 2 hình thức bên dưới."}
                </div>
              </div>
              <button
                onClick={onClose}
                disabled={submitting}
                className={`w-9 h-9 rounded-xl hover:bg-white/8 flex items-center justify-center text-white/55 hover:text-white transition shrink-0 ${
                  submitting ? "invisible pointer-events-none" : ""
                }`}
                data-testid="rating-video-modal-close"
                aria-label="Đóng"
                aria-hidden={submitting ? "true" : "false"}
                tabIndex={submitting ? -1 : 0}
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            {/* Phase 85.A.141 — Sticky upload progress: % + speed MB/s + ETA.
                Hiện khi đang submit + có bytesTotal > 0 (loại trừ flow drive). */}
            {submitting && progress.bytesTotal > 0 && (
              <StickyUploadHeader progress={progress} />
            )}

            {/* Body */}
            <div className="flex-1 overflow-y-auto min-h-0 px-5 py-5 space-y-4">
              {step === 1 ? (
                <>
                  <ol className="space-y-2.5" data-testid="rating-video-guide-list">
                    {[
                      "Quay video bạn đang thi đấu Pickleball — tối thiểu 3 phút, tối đa 15 phút.",
                      "Định dạng MP4 / MOV / WebM / MKV, tối đa 1GB/file.",
                      "Video phải được quay trong vòng 30 ngày gần nhất để BTC chấm điểm chính xác.",
                      "Hãy thi đấu đúng khả năng thực tế — gian dối sẽ bị huỷ điểm + cấm thi đấu giải.",
                      "Bạn có thể: (a) upload trực tiếp lên cloud, hoặc (b) dán link Google Drive có sẵn.",
                    ].map((txt, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[13px] text-white/85 leading-relaxed">
                        <span className="w-5 h-5 rounded-full bg-blue-500/25 border border-blue-400/40 text-blue-200 text-[10px] font-black flex items-center justify-center shrink-0 mt-0.5">
                          {i + 1}
                        </span>
                        <span>{txt}</span>
                      </li>
                    ))}
                  </ol>
                  <div className="rounded-xl border border-amber-400/30 bg-amber-500/8 p-3 text-[11.5px] text-amber-100 leading-snug" data-testid="rating-video-guide-warning">
                    ⚠️ <strong>Bạn được nộp tối đa 3 lần</strong>. Sau đó vui lòng chờ BTC review (24-72h). BTC sẽ xem video để xác định điểm trình chính xác cho bạn.
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-400 hover:to-cyan-400 text-white text-[13px] font-black flex items-center justify-center gap-2 transition shadow-[0_6px_18px_rgba(59,130,246,0.4)]"
                    data-testid="rating-video-step-next"
                  >
                    Đã đọc, tiếp tục
                    <ArrowRight size={14} weight="bold" />
                  </button>
                </>
              ) : (
                <>
                  {/* Phase 85.A.162 — Mode switch: gộp Drive/YT/FB thành 1 tab "Link" */}
                  <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-black/40 border border-white/8" data-testid="rating-video-mode-switch">
                    <button
                      type="button"
                      onClick={() => setMode("upload")}
                      disabled={submitting}
                      className={`h-9 rounded-lg text-[11.5px] font-black flex items-center justify-center gap-1.5 transition ${
                        mode === "upload"
                          ? "bg-gradient-to-br from-blue-500/30 to-cyan-500/15 text-blue-200 border border-blue-400/40"
                          : "text-white/55 hover:text-white"
                      }`}
                      data-testid="rating-video-mode-upload"
                    >
                      <CloudArrowUp size={13} weight="bold" />
                      Upload
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMode("link");
                        // Phase 85.A.166 — Trigger auto-paste ngay trong gesture
                        // context của onClick (iOS Safari yêu cầu).
                        tryPasteFromClipboard(false);
                      }}
                      disabled={submitting}
                      className={`h-9 rounded-lg text-[11.5px] font-black flex items-center justify-center gap-1.5 transition ${
                        mode === "link"
                          ? "bg-gradient-to-br from-purple-500/30 to-fuchsia-500/15 text-purple-200 border border-purple-400/40"
                          : "text-white/55 hover:text-white"
                      }`}
                      data-testid="rating-video-mode-link"
                    >
                      <LinkIcon size={13} weight="bold" />
                      Link YouTube / FB / TikTok / Drive
                    </button>
                  </div>

                  {mode === "upload" ? (
                    <div className="space-y-2.5">
                      <div className="text-[10.5px] font-bold uppercase tracking-wider text-white/45">File video</div>
                      {file ? (
                        <div className="rounded-2xl border border-blue-400/35 bg-blue-500/[0.07] p-3 flex items-center gap-3" data-testid="rating-video-file-preview">
                          <VideoCamera size={28} weight="duotone" className="text-blue-300 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-[12.5px] font-bold text-white truncate">{file.name}</div>
                            <div className="text-[10.5px] text-white/55">{(file.size / 1024 / 1024).toFixed(1)} MB · {file.type || "video"}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFile(null)}
                            disabled={submitting}
                            className="w-7 h-7 rounded-full bg-black/40 hover:bg-rose-500/50 flex items-center justify-center text-white/65 hover:text-white"
                            data-testid="rating-video-file-clear"
                          >
                            <X size={12} weight="bold" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => fileRef.current?.click()}
                          disabled={submitting}
                          className="w-full h-24 rounded-2xl border-2 border-dashed border-blue-400/40 hover:border-blue-400 bg-blue-500/[0.04] hover:bg-blue-500/[0.10] transition flex flex-col items-center justify-center gap-1 text-blue-300"
                          data-testid="rating-video-file-pick"
                        >
                          <CloudArrowUp size={26} weight="duotone" />
                          <span className="text-[12.5px] font-black">Chọn video từ máy</span>
                          <span className="text-[10px] text-white/50">MP4 · MOV · WebM · MKV — Tối đa 1GB · 3-15 phút</span>
                        </button>
                      )}
                      <input
                        ref={fileRef}
                        type="file"
                        accept={ACCEPT}
                        onChange={onPickFile}
                        className="hidden"
                        data-testid="rating-video-file-input"
                      />
                    </div>
                  ) : (
                    /* Phase 85.A.162 — Mode "link": 1 input, auto-detect YT/FB/Drive */
                    <div className="space-y-2">
                      <div className="text-[10.5px] font-bold uppercase tracking-wider text-white/45 flex items-center justify-between">
                        <span>Link video (YouTube / Facebook / TikTok / Google Drive)</span>
                        {linkSource !== "unknown" && (
                          <span className={`px-1.5 py-0.5 rounded text-[9.5px] font-black border ${
                            linkSource === "youtube"
                              ? "bg-rose-500/15 border-rose-400/40 text-rose-200"
                              : linkSource === "facebook"
                              ? "bg-blue-500/15 border-blue-400/40 text-blue-200"
                              : linkSource === "tiktok"
                              ? "bg-pink-500/15 border-pink-400/40 text-pink-200"
                              : "bg-emerald-500/15 border-emerald-400/40 text-emerald-200"
                          }`}>
                            {linkSource === "youtube" ? "🔴 YouTube"
                              : linkSource === "facebook" ? "🔵 Facebook"
                              : linkSource === "tiktok" ? "⚫ TikTok"
                              : "📁 Drive"} detected
                          </span>
                        )}
                      </div>
                      <div className="relative">
                        <input
                          type="url"
                          value={linkUrl}
                          onChange={(e) => setLinkUrl(e.target.value)}
                          placeholder="https://youtu.be/… , facebook.com/watch?v=… , tiktok.com/@user/video/… , drive.google.com/file/d/…"
                          disabled={submitting}
                          className={`w-full h-11 pl-3 pr-[88px] rounded-xl bg-black/40 border text-white text-[12.5px] focus:outline-none transition ${
                            linkSource === "youtube" ? "border-rose-400/50 focus:border-rose-400" :
                            linkSource === "facebook" ? "border-blue-400/50 focus:border-blue-400" :
                            linkSource === "tiktok" ? "border-pink-400/50 focus:border-pink-400" :
                            linkSource === "drive" ? "border-emerald-400/50 focus:border-emerald-400" :
                            "border-white/15 focus:border-purple-400/60"
                          }`}
                          data-testid="rating-video-link-input"
                        />
                        {/* Phase 85.A.166 — Nút "Dán" thủ công cho trường hợp iOS Safari
                            denied clipboard permission lần đầu hoặc user muốn override input. */}
                        <button
                          type="button"
                          onClick={() => tryPasteFromClipboard(true)}
                          disabled={submitting}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 px-2.5 rounded-lg bg-purple-500/20 hover:bg-purple-500/35 border border-purple-400/40 text-purple-200 text-[10.5px] font-black inline-flex items-center gap-1 transition disabled:opacity-40"
                          data-testid="rating-video-link-paste-btn"
                          title="Dán link từ clipboard"
                        >
                          📋 Dán
                        </button>
                      </div>
                      {/* Preview thumbnail cho YT (FB không có public thumbnail, Drive cũng vậy) */}
                      {(() => {
                        const m = (linkUrl || "").match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/i);
                        const ytId = m ? m[1] : null;
                        if (!ytId) return null;
                        return (
                          <div className="mt-2 rounded-xl overflow-hidden border border-rose-400/30 bg-black aspect-video" data-testid="rating-video-yt-preview">
                            <img
                              src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`}
                              alt="YouTube thumbnail"
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        );
                      })()}
                      <p className="text-[10.5px] text-white/45 leading-snug space-y-1">
                        💡 <strong>YouTube/Facebook/TikTok</strong>: video phải <strong>Public</strong> hoặc <strong>Unlisted</strong>. BTC xem trực tiếp qua player Pickleague.
                        <br />💡 <strong>Drive</strong>: share quyền <strong>Anyone with the link</strong>. Hệ thống sẽ tải về DO Spaces tự động.
                      </p>
                    </div>
                  )}

                  {/* Notes (bắt buộc — tối thiểu 20 ký tự) */}
                  <div data-testid="rating-video-notes-block">
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-white/45 mb-1.5 flex items-center gap-1.5">
                      Ghi chú <span className="text-rose-400">*</span>
                    </div>
                    <textarea
                      ref={notesRef}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      disabled={submitting}
                      rows={3}
                      maxLength={500}
                      placeholder="Vd: Đây là video thi đấu của tôi cách đây 1 tuần, tôi là VĐV mặc áo đỏ, đi giày màu trắng"
                      className={`w-full p-2.5 rounded-xl bg-black/40 border text-white text-[12px] focus:outline-none resize-y transition-all duration-200 ${
                        notesShake
                          ? "border-rose-400 ring-2 ring-rose-400/60 animate-pulse shadow-[0_0_0_4px_rgba(244,63,94,0.18)]"
                          : "border-white/12 focus:border-blue-400/50"
                      }`}
                      data-testid="rating-video-notes"
                    />
                    <div className="mt-1 text-[10.5px] text-white/45 flex items-center justify-between">
                      <span>Bắt buộc — mô tả ngắn giúp BTC nhận diện bạn trong video.</span>
                      <span className={notes.trim().length < NOTES_MIN ? "text-rose-300" : "text-emerald-300"}>{notes.trim().length}/{NOTES_MIN}+</span>
                    </div>
                  </div>

                  {/* Commitments */}
                  <div className="space-y-2" data-testid="rating-video-commitments">
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-white/45">Cam kết bắt buộc (tick đủ {commitmentItems.length})</div>
                    {commitmentItems.map((item) => (
                      <label
                        key={item.key}
                        className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition ${
                          item.danger
                            ? "border-rose-400/45 bg-rose-500/[0.07] hover:border-rose-300/70 hover:bg-rose-500/[0.12]"
                            : "border-white/10 hover:border-white/25 bg-white/[0.02]"
                        }`}
                        data-testid={`rating-video-commitment-${item.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!commitments[item.key]}
                          onChange={(e) => setCommitments({ ...commitments, [item.key]: e.target.checked })}
                          disabled={submitting}
                          className={`w-4 h-4 mt-0.5 shrink-0 ${item.danger ? "accent-rose-500" : "accent-blue-500"}`}
                        />
                        <span className={`text-[12px] leading-snug ${item.danger ? "text-rose-100 font-bold" : "text-white/85"}`}>{item.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Progress */}
                  {submitting && progress.stage !== "idle" && (
                    <UploadProgressBar
                      pct={progress.pct || 0}
                      label={progress.label}
                      stage={progress.stage === "done" ? "done" : "uploading"}
                      testId="rating-video-progress"
                    />
                  )}

                  {serverError && (
                    <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 p-3 text-[11.5px] text-rose-100 flex items-start gap-2" data-testid="rating-video-server-error">
                      <Warning size={14} weight="fill" className="text-rose-300 shrink-0 mt-0.5" />
                      <span>{serverError}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            {step === 2 && (
              <div className="px-5 py-3.5 border-t border-white/8 bg-black/30 flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  disabled={submitting}
                  className="h-10 px-3 rounded-xl border border-white/15 hover:bg-white/5 text-white/70 text-[12px] font-bold flex items-center gap-1.5 transition disabled:opacity-40"
                  data-testid="rating-video-step-back"
                >
                  <ArrowLeft size={12} weight="bold" />
                  Quay lại
                </button>
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={mode === "upload" ? submitUpload : submitLink}
                  disabled={submitting || !allCommitted || (
                    mode === "upload" ? !file : !linkUrl.trim() || linkSource === "unknown"
                  )}
                  className="h-10 px-4 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-400 hover:to-cyan-400 text-white text-[12.5px] font-black flex items-center gap-1.5 transition disabled:opacity-40 shadow-[0_6px_18px_rgba(59,130,246,0.4)]"
                  data-testid="rating-video-submit"
                >
                  {submitting ? (
                    <>
                      <span className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                      Đang gửi…
                    </>
                  ) : (
                    <>
                      <CheckCircle size={13} weight="fill" />
                      {isPersonal ? "Tải Video lên" : "Nộp video"}
                    </>
                  )}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
